import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Textarea,
  Radio,
  RadioGroup,
  Spinner,
  useToast,
  List,
  ListItem,
  Avatar,
} from '@chakra-ui/react';
import { Globe, Users, Lock, Send, RefreshCw } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import { createStory, formatWinAmount, getWinTier } from '../../services/storyService';
import { searchUsers } from '../../services/socialService';

// Opponent info for the win card
export interface OpponentInfo {
  odD: string;
  username: string;
  hand?: string; // Their losing hand type
  folded?: boolean;
}

interface ShareWinModalProps {
  isOpen: boolean;
  onClose: () => void;
  potAmount: number;
  currency: 'pkcash' | 'playcoins';
  handType?: string;
  tableName?: string;
  userId: string;
  username: string;
  avatarUrl?: string;
  // NEW: Opponent data
  opponents?: OpponentInfo[];
  wasShowdown?: boolean; // true if went to showdown, false if fold win
}

const ShareWinModal: React.FC<ShareWinModalProps> = ({
  isOpen,
  onClose,
  potAmount,
  currency,
  handType,
  tableName,
  userId,
  username,
  avatarUrl,
  opponents = [],
  wasShowdown = false,
}) => {
  const toast = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'friends' | 'private'>('public');
  const [isPosting, setIsPosting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  
  // @ mention state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState<{odD: string, username: string, photoURL?: string}[]>([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);

  const winTier = getWinTier(potAmount, currency);
  const formattedAmount = formatWinAmount(potAmount, currency);

  // Generate win card image when modal opens
  useEffect(() => {
    if (isOpen && !imageUrl) {
      generateWinCard();
    }
  }, [isOpen]);

  // Cleanup URL on close
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  // Search for users when @ is typed
  useEffect(() => {
    if (!mentionQuery || mentionQuery.length < 1) {
      setMentionResults([]);
      return;
    }

    const searchTimer = setTimeout(async () => {
      setMentionLoading(true);
      try {
        const results = await searchUsers(mentionQuery, 5);
        setMentionResults(results.map(u => ({
          odD: u.odwinId || u.odD || '',
          username: u.username,
          photoURL: u.photoURL,
        })));
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setMentionLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [mentionQuery]);

  // Handle caption change with @ detection
  const handleCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart || 0;
    setCaption(value);
    setCursorPosition(cursor);

    // Check if we're typing after an @
    const textBeforeCursor = value.slice(0, cursor);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  // Insert mention into caption
  const insertMention = (user: {username: string}) => {
    const textBeforeCursor = caption.slice(0, cursorPosition);
    const textAfterCursor = caption.slice(cursorPosition);
    
    // Replace the @query with @username
    const newTextBefore = textBeforeCursor.replace(/@\w*$/, `@${user.username} `);
    const newCaption = newTextBefore + textAfterCursor;
    
    setCaption(newCaption);
    setShowMentions(false);
    setMentionQuery('');
    
    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const generateWinCard = async () => {
    setIsGenerating(true);
    
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      // Card dimensions (Instagram story size)
      const width = 1080;
      const height = 1350;
      canvas.width = width;
      canvas.height = height;
      
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(0.5, '#16213e');
      gradient.addColorStop(1, '#0f3460');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Add subtle pattern overlay
      ctx.fillStyle = 'rgba(0, 217, 217, 0.03)';
      for (let i = 0; i < 50; i++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        ctx.beginPath();
        ctx.arc(x, y, Math.random() * 100 + 20, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Top decorative line
      ctx.strokeStyle = POKER_COLORS.gold;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(100, 80);
      ctx.lineTo(width - 100, 80);
      ctx.stroke();
      
      // Trophy emoji
      ctx.font = 'bold 120px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('', width / 2, 220);

      
      // "BIG WIN" text
      ctx.font = 'bold 72px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = 'rgba(0, 217, 217, 0.5)';
      ctx.shadowBlur = 20;
      ctx.fillText('BIG WIN!', width / 2, 320);
      ctx.shadowBlur = 0;
      
      // Username
      ctx.font = 'bold 44px Arial';
      ctx.fillStyle = POKER_COLORS.cyan;
      ctx.fillText(username, width / 2, 400);
      
      // Win amount box
      const boxY = 440;
      const boxHeight = 160;
      ctx.fillStyle = 'rgba(255, 215, 0, 0.15)';
      ctx.strokeStyle = POKER_COLORS.gold;
      ctx.lineWidth = 3;
      roundRect(ctx, 140, boxY, width - 280, boxHeight, 20);
      ctx.fill();
      ctx.stroke();
      
      // Win amount
      ctx.font = 'bold 80px Arial';
      ctx.fillStyle = POKER_COLORS.gold;
      ctx.shadowColor = 'rgba(255, 215, 0, 0.5)';
      ctx.shadowBlur = 15;
      ctx.fillText(formattedAmount, width / 2, boxY + 75);
      ctx.shadowBlur = 0;
      
      // Currency label
      ctx.font = '32px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins', width / 2, boxY + 130);
      
      let currentY = boxY + boxHeight + 50;
      
      // Hand type if available (showdown)
      if (handType && wasShowdown) {
        ctx.font = 'bold 40px Arial';
        ctx.fillStyle = '#ffffff';
        ctx.fillText(`with ${handType}`, width / 2, currentY);
        currentY += 50;
      }
      
      // === NEW: DEFEATED OPPONENTS SECTION ===
      if (opponents.length > 0) {
        currentY += 20;
        
        // "Defeated" header
        ctx.font = 'bold 32px Arial';
        ctx.fillStyle = '#ef4444'; // Red
        ctx.fillText(' DEFEATED', width / 2, currentY);

        currentY += 45;
        
        // Show opponents (max 3)
        const displayOpponents = opponents.slice(0, 3);
        
        for (const opp of displayOpponents) {
          ctx.font = '28px Arial';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          
          let oppText = opp.username;
          if (opp.folded) {
            oppText += ' (folded)';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
          } else if (opp.hand && wasShowdown) {
            oppText += ` - ${opp.hand}`;
          }
          
          ctx.fillText(oppText, width / 2, currentY);
          currentY += 38;
        }
        
        // Show "+X more" if there are more opponents
        if (opponents.length > 3) {
          ctx.font = '24px Arial';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
          ctx.fillText(`+${opponents.length - 3} more`, width / 2, currentY);
          currentY += 35;
        }
      }
      
      // Table name
      if (tableName) {
        currentY += 20;
        ctx.font = '28px Arial';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.fillText(`at  ${tableName}`, width / 2, currentY);

        currentY += 50;
      }
      
      // Win tier badge
      const tierColors: Record<string, string> = {
        monster: '#FFD700',
        big: '#9333ea',
        normal: '#22c55e',
      };
      const tierLabels: Record<string, string> = {
        monster: 'LEGENDARY WIN',
        big: 'EPIC WIN',
        normal: 'NICE WIN',
      };
      const tierColor = tierColors[winTier] || tierColors.normal;
      const tierLabel = tierLabels[winTier] || tierLabels.normal;
      
      ctx.fillStyle = tierColor;
      ctx.font = 'bold 32px Arial';
      ctx.fillText(tierLabel, width / 2, currentY + 30);
      
      // Bottom decorative line
      ctx.strokeStyle = POKER_COLORS.gold;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(100, height - 180);
      ctx.lineTo(width - 100, height - 180);
      ctx.stroke();
      
      // Branding
      ctx.font = 'bold 36px Arial';
      ctx.fillStyle = POKER_COLORS.cyan;
      ctx.fillText('PokerSweepStars', width / 2, height - 110);
      
      ctx.font = '24px Arial';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.fillText('pokersweepstars.web.app', width / 2, height - 65);
      
      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          setImageBlob(blob);
          setImageUrl(URL.createObjectURL(blob));
        }
      }, 'image/jpeg', 0.95);
      
    } catch (error) {
      console.error('Failed to generate win card:', error);
      toast({
        title: 'Generation failed',
        description: 'Could not create win card.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Helper function for rounded rectangles
  const roundRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  const handlePost = async () => {
    if (!imageBlob) {
      toast({
        title: 'Not ready',
        description: 'Please wait for the image to generate.',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setIsPosting(true);
    try {
      await createStory(
        userId,
        username,
        avatarUrl,
        imageBlob,
        potAmount,
        currency,
        handType,
        tableName,
        caption.trim() || undefined,
        visibility
      );

      toast({
        title: 'Story Posted!',

        description: 'Your win has been shared.',
        status: 'success',
        duration: 3000,
      });

      onClose();
    } catch (error) {
      console.error('Failed to post story:', error);
      toast({
        title: 'Post failed',
        description: error instanceof Error ? error.message : 'Could not post your story.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg={POKER_COLORS.darkBase} maxW="500px" mx={4}>
        <ModalHeader color="white" borderBottom="1px solid" borderColor="whiteAlpha.100">
          <HStack>
            <Text fontSize="xl"> Share Your Win</Text>

          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" />
        
        <ModalBody py={6}>
          <VStack spacing={5} align="stretch">
            {/* Win Card Preview */}
            <Box
              position="relative"
              borderRadius="lg"
              overflow="hidden"
              bg="gray.900"
              minH="300px"
            >
              {/* Hidden canvas for generation */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              
              {isGenerating ? (
                <VStack py={20} spacing={4}>
                  <Spinner size="xl" color={POKER_COLORS.cyan} />
                  <Text color="gray.400">Creating your win card...</Text>
                </VStack>
              ) : imageUrl ? (
                <Box position="relative">
                  <img
                    src={imageUrl}
                    alt="Win card"
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '400px',
                      objectFit: 'contain',
                    }}
                  />
                  <Button
                    position="absolute"
                    top={2}
                    right={2}
                    size="sm"
                    leftIcon={<RefreshCw size={14} />}
                    onClick={generateWinCard}
                    colorScheme="whiteAlpha"
                  >
                    Regenerate
                  </Button>
                </Box>
              ) : (
                <VStack py={20} spacing={4}>
                  <Text color="gray.500">Failed to generate preview</Text>
                  <Button onClick={generateWinCard} colorScheme="cyan" size="sm">
                    Try Again
                  </Button>
                </VStack>
              )}
            </Box>

            {/* Caption Input with @ Mentions */}
            <Box position="relative">
              <Text color="cyan.300" fontSize="sm" mb={2} fontWeight="medium">
                Caption (optional) - Use @ to tag players
              </Text>
              <Textarea
                ref={textareaRef}
                placeholder="Add a caption... @username to tag"
                value={caption}
                onChange={handleCaptionChange}
                bg="gray.800"
                border="2px solid"
                borderColor="gray.600"
                color="white"
                fontSize="md"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ borderColor: POKER_COLORS.cyan, bg: 'gray.700' }}
                _hover={{ borderColor: 'gray.500' }}
                maxLength={200}
                rows={2}
                resize="none"
              />
              <Text color="gray.400" fontSize="xs" textAlign="right" mt={1}>
                {caption.length}/200
              </Text>
              
              {/* @ Mention Dropdown */}
              {showMentions && (
                <Box
                  position="absolute"
                  bottom="100%"
                  left={0}
                  right={0}
                  bg="gray.800"
                  border="1px solid"
                  borderColor="gray.600"
                  borderRadius="md"
                  maxH="200px"
                  overflowY="auto"
                  zIndex={10}
                  mb={1}
                >
                  {mentionLoading ? (
                    <HStack p={3} justify="center">
                      <Spinner size="sm" color={POKER_COLORS.cyan} />
                      <Text color="gray.400" fontSize="sm">Searching...</Text>
                    </HStack>
                  ) : mentionResults.length > 0 ? (
                    <List spacing={0}>
                      {mentionResults.map((user) => (
                        <ListItem
                          key={user.odD}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'gray.700' }}
                          onClick={() => insertMention(user)}
                        >
                          <HStack>
                            <Avatar size="xs" name={user.username} src={user.photoURL} />
                            <Text color="white" fontSize="sm">@{user.username}</Text>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  ) : mentionQuery ? (
                    <Text color="gray.500" fontSize="sm" p={3}>
                      No users found for "@{mentionQuery}"
                    </Text>
                  ) : null}
                </Box>
              )}
            </Box>

            {/* Visibility Options */}
            <Box>
              <Text color="cyan.300" fontSize="sm" mb={2} fontWeight="medium">
                Who can see this?
              </Text>
              <RadioGroup value={visibility} onChange={(v) => setVisibility(v as any)}>
                <VStack align="stretch" spacing={2}>
                  <Box
                    as="label"
                    p={3}
                    borderRadius="md"
                    bg={visibility === 'public' ? 'cyan.900' : 'gray.800'}
                    border="2px solid"
                    borderColor={visibility === 'public' ? POKER_COLORS.cyan : 'gray.600'}
                    cursor="pointer"
                    _hover={{ borderColor: 'gray.500' }}
                  >
                    <HStack>
                      <Radio value="public" colorScheme="cyan" />
                      <Globe size={18} color={POKER_COLORS.cyan} />
                      <VStack align="start" spacing={0}>
                        <Text color="white" fontSize="sm" fontWeight="medium">Everyone</Text>
                        <Text color="gray.400" fontSize="xs">Anyone can see your win</Text>
                      </VStack>
                    </HStack>
                  </Box>

                  <Box
                    as="label"
                    p={3}
                    borderRadius="md"
                    bg={visibility === 'friends' ? 'cyan.900' : 'gray.800'}
                    border="2px solid"
                    borderColor={visibility === 'friends' ? POKER_COLORS.cyan : 'gray.600'}
                    cursor="pointer"
                    _hover={{ borderColor: 'gray.500' }}
                  >
                    <HStack>
                      <Radio value="friends" colorScheme="cyan" />
                      <Users size={18} color={POKER_COLORS.cyan} />
                      <VStack align="start" spacing={0}>
                        <Text color="white" fontSize="sm" fontWeight="medium">Friends Only</Text>
                        <Text color="gray.400" fontSize="xs">Only mutual friends can see</Text>
                      </VStack>
                    </HStack>
                  </Box>

                  <Box
                    as="label"
                    p={3}
                    borderRadius="md"
                    bg={visibility === 'private' ? 'cyan.900' : 'gray.800'}
                    border="2px solid"
                    borderColor={visibility === 'private' ? POKER_COLORS.cyan : 'gray.600'}
                    cursor="pointer"
                    _hover={{ borderColor: 'gray.500' }}
                  >
                    <HStack>
                      <Radio value="private" colorScheme="cyan" />
                      <Lock size={18} color={POKER_COLORS.cyan} />
                      <VStack align="start" spacing={0}>
                        <Text color="white" fontSize="sm" fontWeight="medium">Save Privately</Text>
                        <Text color="gray.400" fontSize="xs">Only you can see this</Text>
                      </VStack>
                    </HStack>
                  </Box>
                </VStack>
              </RadioGroup>
            </Box>

            {/* Post Button */}
            <Button
              leftIcon={<Send size={18} />}
              colorScheme="yellow"
              size="lg"
              w="full"
              onClick={handlePost}
              isLoading={isPosting}
              loadingText="Posting..."
              isDisabled={!imageBlob || isGenerating}
            >
              Post to Story
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ShareWinModal;